import {
	DiscoverClubHouseGetResponse,
} from "libs/apiClient"
import { ContentCell } from "./ContentCell";
import client from "libs/client";
import {UserPermissionsTable} from "features/discover/UserPermissionsTable";
import {ClubhouseTeamsTable} from "features/discover/ClubHouseTeamsTable";
import {InviteLinksTable} from "features/discover/IviteLinksTable";
import {InviteLinksHistoryTable} from "features/discover/IviteLinksHistoryTable";
import {useId, useState} from "react";
import {DiscoverGamesTable} from "features/discover/DiscoverGamesTable";
import {Tabs} from "features/discover/Tabs";
import {ClubTokenMovementsTable} from "features/discover/ClubTokenMovementsTable";
import { DiscoverSubscriptionsTable } from "features/discover/DiscoverSubscriptionsTable";
import { DiscoverTeamGroupsTable } from "features/discover/DiscoverTeamGroupsTable";
import { Link } from "react-router";

export function DiscoverClubHouseDetail({ clubHouseData }: { clubHouseData: DiscoverClubHouseGetResponse }) {
	const [tab, setTab] = useState<string | null>("games");

	const contentProps = {
		mutation: client.patchDiscoverClubHouse.useMutation(),
		invalidationKey: 'discoverClubHouseDetail',
		id: clubHouseData.id,
	};

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({});

	const teams = clubHouseData?.teams || [];
	const permissions = clubHouseData?.userPermissions || [];
	const inviteLinks = clubHouseData?.inviteLinks || [];
	const inviteLinksHistory = clubHouseData?.inviteLinksHistory;

	const columns = [
		{
			title: "Club Name",
			field: clubHouseData.isDiscover ? "name" : undefined,
			type: "string",
			value: clubHouseData.name,
		},
		{
			title: "Country",
			field: "country",
			type: "list",
			value: clubHouseData.country?.id,
			isSelectLoading: isCountriesLoading,
			options: countries?.data || [],
		},
		...(!clubHouseData.isDiscover ? [{
			title: "Player app disabled",
			field: "isPlayerAppDisabled",
			type: "boolean",
			formatter: () => <span className={clubHouseData.isPlayerAppDisabled ? "false" : "true"}>{clubHouseData.isPlayerAppDisabled ? "Disabled" : "Enabled"}</span>,
		}] : []),
		...(clubHouseData.entityType === "Partner" ? [{
			title: "Club page",
			type: "link",
			formatter: () => <Link to={`/admin/club-amateur/${clubHouseData.id}`} className="button-link" target="_blank">Partner ({clubHouseData.id})</Link>,
		}] : []),
		...(clubHouseData.entityType === "NonPartner" ? [{
			title: "Club page",
			type: "link",
			formatter: () => <Link to={`/admin/club-amateur-nonpartner/${clubHouseData.id}`} className="button-link" target="_blank">Non-Partner ({clubHouseData.id})</Link>,
		}] : []),
		...(!clubHouseData.isDiscover ? [{
			title: "Elite Type",
			field: "isNational",
			value: clubHouseData.isNational,
			type: "boolean",
			formatter: () => <span>{clubHouseData.isNational ? "National" : "League"}</span>,
			options: [
				{
					label: "League",
					value: false,
				}, {
					label: "National",
					value: true,
				},
			],
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "Region",
			field: "region",
			type: "string",
			value: clubHouseData.region,
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "City",
			field: "city",
			type: "string",
			value: clubHouseData.city,
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "Use media for marketing purpose",
			field: "useMediaForMarketingPurpose",
			type: "boolean",
			value: clubHouseData.useMediaForMarketingPurpose,
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "Player access to metrics",
			field: "hasPlayerAccessToMetrics",
			type: "boolean",
			value: clubHouseData.hasPlayerAccessToMetrics,
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "Player access to ratings",
			field: "hasPlayerAccessToRatings",
			type: "boolean",
			value: clubHouseData.hasPlayerAccessToRatings,
		}] : []),
		...(clubHouseData.isDiscover ? [{
			title: "Claimed number of teams",
			field: "numberOfTeams",
			type: "number",
			value: clubHouseData.numberOfTeams,
		}] : []),
	];

	const tabs = [
		{
			label: "Games",
			key: "games"
		}, {
			label: "Teams",
			key: "teams",
			disabled: teams.length === 0
		}, {
			label: "Permissions",
			key: "permissions",
			disabled: permissions.length === 0
		}, {
			label: "Invite links",
			key: "inviteLinks",
			disabled: inviteLinks.length === 0
		}, {
			label: "Invite links history",
			key: "inviteLinksHistory",
			disabled: inviteLinksHistory.length === 0
		}, {
			label: "Token movements",
			key: "tokenMovements",
			disabled: !clubHouseData.tokenReadAccess
		}, {
			label: "Subscriptions",
			key: "subscriptions",
			disabled: !clubHouseData.subscriptionReadAccess,
		}, {
			label: "Team groups",
			key: "teamGroups",
			disabled: !clubHouseData.isDiscover || !clubHouseData.teamGroupReadAccess,
		},
	];

    return (
		<>
			<div className="discover-card__title">Basic clubhouse data</div>
			<div className="discover-card">
				<div className="cell-grid">
					{columns.map((props) => {
						const key = useId();

						return <ContentCell key={key} {...contentProps} {...props} />
					})}
				</div>
			</div>

			<Tabs items={tabs} active={tab} onClick={(key) => setTab(key)} />

			<div className="discover-card discover-card--for-table" style={{display: tab === "games" ? "block" : "none"}}>
				<DiscoverGamesTable defaultFilters={{
					anyClubId: clubHouseData.id,
				}} columnsVisibilityKey="columnsVisibilityClubHouse" />
			</div>

			{tab === "teams" && <div className="discover-card">
				<ClubhouseTeamsTable teams={teams}/>
			</div>}

			{tab === "permissions" && <div className="discover-card">
				<UserPermissionsTable userPermissions={permissions}/>
			</div>}

			{tab === "inviteLinks" && <div className="discover-card">
				<InviteLinksTable inviteLinks={inviteLinks}/>
			</div>}

			{tab === "inviteLinksHistory" && <div className="discover-card">
				<InviteLinksHistoryTable inviteLinksHistory={inviteLinksHistory}/>
			</div>}

			{tab === "tokenMovements" && <div className="discover-card">
				<ClubTokenMovementsTable
					clubId={clubHouseData.id}
					tokenBalance={clubHouseData?.tokenBalance || 0}
					tokenMovements={clubHouseData?.tokenMovements || []}
					tokenEditAccess={clubHouseData.tokenEditAccess}
				/>
			</div>}

			{tab === "subscriptions" && <div className="discover-card">
				<DiscoverSubscriptionsTable clubId={clubHouseData.id} subscriptionEditAccess={clubHouseData.subscriptionEditAccess} />
			</div>}

			{tab === "teamGroups" && <div className="discover-card discover-card--for-table">
				<DiscoverTeamGroupsTable clubId={clubHouseData.id} teamGroupEditAccess={clubHouseData.teamGroupEditAccess} />
			</div>}
		</>
	)
}
